/**
 * Table — the in-game screen. Owner: Phase-3 A3.1 (table scene). Composes
 * the table layout (Felt + Seats + HUD + center trick area + own hand) plus
 * the per-phase overlays (BiddingPanel, Announce, RoundSummary, GameOver).
 *
 * Composition contract with A3.2 (parallel agent):
 *   - <Hand />, <Trick />, <BiddingPanel />, <Announce /> are owned by A3.2.
 *   - This file only IMPORTS them; it never edits them. They are props-less
 *     and read everything they need from the store hooks.
 *
 * Required surface (PRD §5.5 FR-U1..FR-U6, FR-G7; RULES §1.1/§4/§5/§6/§7):
 *   - Oval felt, local human bottom-center, turn order to the right.
 *   - Opponent seat rings around the table (hand-count chip, turn indicator,
 *     partner highlight, bot badge, connected dot, caller marker).
 *   - HUD: round/total, trump, current bid + contract team, tricks per team,
 *     break-even and win targets (always visible).
 *   - Center trick area: cards as they are played, sweep to the winner.
 *   - Bidding panel when phase==='bidding' and yourTurn.
 *   - Joker-2 removed announcement when relevant (4p jokers-on, RULES §2).
 *   - Round summary overlay on phase==='round_end'.
 *   - Game-over summary overlay on view.gameOver.
 *
 * Seat layout (RULES §1.1): layoutSlot = (seatIndex - you + n) % n. Slot 0 =
 * bottom = you; further slots proceed clockwise. Partners:
 *   - 4p: opposite seat = (you + 2) mod 4.
 *   - 6p: two teammates at (you + 2) and (you + 4) mod 6.
 */
import { Announce } from "./Announce.js";
import { BiddingPanel } from "./BiddingPanel.js";
import { Felt } from "./Felt.js";
import { GameOver } from "./GameOver.js";
import { HUD } from "./HUD.js";
import { Hand } from "./Hand.js";
import { RoundSummary } from "./RoundSummary.js";
import { Seat } from "./Seat.js";
import { Trick } from "./Trick.js";
import { WonPilesRail } from "./WonPilesRail.js";
import { useLobby, useView, useYou } from "../state/hooks.js";

/**
 * Map an engine seat index to a screen layout slot. Slot 0 = bottom-center =
 * local player; further slots proceed clockwise around the oval. Turn order
 * (engine: i -> (i+1)%n) flows to the local player's right.
 */
function layoutSlotFor(seatIndex: number, you: number, n: number): number {
  return ((seatIndex - you) % n + n) % n;
}

/**
 * Partners on the local human's team. 4p: the seat directly opposite (you+2).
 * 6p: the two teammates at (you+2, you+4). Tests live in fixtures (engine
 * §1.2: seats with the same parity are teammates).
 */
function partnerSeats(you: number, n: number): Set<number> {
  if (n === 4) return new Set([(you + 2) % 4]);
  return new Set([(you + 2) % 6, (you + 4) % 6]);
}

export function Table(): JSX.Element {
  const view = useView();
  const lobby = useLobby();
  const you = useYou();

  if (!view || you === null) {
    return (
      <main className="table-loading" aria-busy="true">
        <p>Loading game…</p>
      </main>
    );
  }

  const n = view.config.players;
  const partners = partnerSeats(you, n);
  // The server serializes `hostId = String(state.hostSeat)` (apps/server/src/
  // room.ts) so the lobby's hostId is the host's seat index as a string.
  // Lobby.tsx already does the same parse; do it here too so the table shows
  // the gold host marker (CSS .seat-host-marker is already defined). Fix per
  // docs/PHASE3-UX-REVIEW.md Finding #5.
  const hostSeatIndex = lobby?.hostId ? Number(lobby.hostId) : Number.NaN;
  const isHostSeat = (seatIndex: number): boolean =>
    Number.isFinite(hostSeatIndex) && seatIndex === hostSeatIndex;

  // Seat layout: render one Seat per seat index, positioned by layoutSlot.
  const seatNodes = [];
  for (let seatIndex = 0; seatIndex < n; seatIndex++) {
    const slot = layoutSlotFor(seatIndex, you, n);
    seatNodes.push(
      <Seat
        key={seatIndex}
        seatIndex={seatIndex}
        layoutSlot={slot}
        isYou={seatIndex === you}
        isPartner={partners.has(seatIndex)}
        isHost={isHostSeat(seatIndex)}
      />,
    );
  }

  // Phase 11 (v1.2): expose the active phase + bidding-open state as data-*
  // attributes on `.table-screen` so the slot-0 hide CSS rule (Phase 8
  // carry-over: bottom seat overlaps with the BiddingPanel) can switch on
  // them without per-seat prop drilling. The bidding panel only renders when
  // `phase === 'bidding' && !bidding.resolved`; we mirror that here.
  const isBiddingOpen = view.phase === "bidding" && !view.bidding.resolved;

  // v1.4.1 Phase 22.2: hand-active modifier. The local hand "breathes" only
  // when the local player has at least one legal CARD play queued (bid moves
  // don't count — the BiddingPanel owns its own urgency cue). Hand.tsx filters
  // legalMoves to `type === "play"` for its own gating; we mirror the
  // predicate here so the class flips at the same moment the cards light up.
  const handActive = view.legalMoves.some((m) => m.type === "play");

  return (
    <main
      className="table-screen"
      aria-labelledby="table-title"
      data-phase={view.phase}
      data-bidding-open={isBiddingOpen ? "true" : undefined}
    >
      <h1 id="table-title" className="sr-only">
        Hookum table
      </h1>

      <HUD />

      {/* Phase 8 (v1.2): WonPilesRails are direct children of `.table` so they
          can claim the `rl`/`rr` grid areas in the desktop ≥ 1024px override.
          They're `display: none` below 1024px (the HUD's hands-won pills
          carry that data on phones). The JSX renders unconditionally for
          snapshot stability. */}
      <WonPilesRail team="A" side="left" />

      <div className="table-body">
        <div className="table-felt-cell">
          <Felt count={n}>
            {seatNodes}
            <div className="table-center" aria-label="Current hand">
              <Trick />
            </div>
          </Felt>
        </div>

        {/* BiddingPanel renders modal-style anchored at the bottom of the
            body cell — preserving its Phase 3/5 visual placement above the
            hand band. The new CSS pins it via position: absolute. */}
        <BiddingPanel />
      </div>

      <WonPilesRail team="B" side="right" />

      <div className={"table-bottom" + (handActive ? " hand-active" : "")}>
        <Hand />
      </div>

      {/* v1.4 Phase 16 (smart bots + remove last-trick peek, 2026-05-21):
          the Phase 11 <LastTrickPeek /> mount was removed here. Owner:
          "in the game you shouldn't be able to look at the last hand."
          The 5-s inter-trick lingering window (RULES §5.5 / Phase 13
          `view.nextTrickAt`) still keeps the just-completed trick on
          the felt — that's the in-band beat, not a rewind. */}

      {/* Overlays — Announce/RoundSummary/GameOver remain position:fixed and
          live outside the grid; render order is z-stacking, not flow order.
          Stacking: bidding/announce first (live), round summary above them
          when the round closes, game over above everything. */}
      <Announce />
      <RoundSummary />
      <GameOver />
    </main>
  );
}
